import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FieldGroup, SuperAdminInfo } from '@/ui'
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalProps,
  ModalTitle,
  Textarea,
  formatHookFormErrors,
} from '@dashboard/ui'
import { useCurrentUser } from '@/api/useCurrentUser'
import { SelectEventsButton } from '@/layouts/webhooks/widgets/SelectEventsButton'
import { WebhookEventType, WebhookRedacted, WebhookRevealed, WebhookUpdate } from '@/api/dashboard'
import { dashboardClient } from '@/api'
import { urlRegexp, useGenerateCompanyPath } from '@/libs'
import { MAX_DESCRIPTION_LENGTH } from '@/Settings'
import { useQueryClient } from '@tanstack/react-query'
import { invalidateWebhookDetailQuery } from '@/layouts/webhooks/api'
import { Divider } from '@/components'

interface EditWebhookModalProps extends ModalProps {
  webhook: WebhookRedacted | WebhookRevealed
}

export const EditWebhookModal = (props: EditWebhookModalProps) => {
  const { ...rest } = props
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { isSuperAdmin } = useCurrentUser()
  const ql = useQueryClient()
  const formMethods = useForm<WebhookUpdate>({
    defaultValues: {
      url: props.webhook.url,
      description: props.webhook.description || '',
      events: props.webhook.events,
      enabled: props.webhook.enabled,
      enabled_logs: props.webhook.enabled_logs,
      player_context_enabled: props.webhook.player_context_enabled,
    },
  })

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setError,
    watch,
  } = formMethods

  const events = watch('events')

  const saveClick = async (data: WebhookUpdate) => {
    if (!data.events?.length) {
      setError('events', { message: t('validation.required') })
      return
    }

    await dashboardClient.v1.updateWebhook(companyId, gameId, props.webhook.id, data)
    await invalidateWebhookDetailQuery(ql, gameId, props.webhook.id)
    props.onClose?.()
  }

  return (
    <Modal {...rest}>
      <form onSubmit={handleSubmit(saveClick)}>
        <ModalContent>
          <ModalTitle>{t('webhook.form.header.edit-title')}</ModalTitle>

          <div
            className="mb-4 text-paragraph-sm text-text-secondary"
            dangerouslySetInnerHTML={{ __html: t('webhook.form.header.sub') }}
          />
          <FieldGroup label={t('webhook.form.endpoint-url')} required={true}>
            <Input
              {...register('url', {
                required: t('validation.required'),
                pattern: { value: urlRegexp, message: t('validation.invalid_url') },
                setValueAs: value => value.trim(),
              })}
              placeholder="https://example.com"
              autoFocus={true}
              {...formatHookFormErrors(errors, 'url')}
            />
          </FieldGroup>

          <FieldGroup label={t('webhook.form.desc')}>
            <Textarea
              {...register('description', {
                maxLength: {
                  message: t('validation.maxLength', {
                    field: t('webhook.form.desc'),
                    value: MAX_DESCRIPTION_LENGTH,
                  }),
                  value: MAX_DESCRIPTION_LENGTH,
                },
              })}
              placeholder={t('webhook.form.description-placeholder')}
              {...formatHookFormErrors(errors, 'description')}
            />
          </FieldGroup>

          <Checkbox {...register('enabled')}>{t('webhook.enabled')}</Checkbox>

          {(events?.includes(WebhookEventType.ItemAdd) || events?.includes(WebhookEventType.ItemRemove)) && (
            <Checkbox {...register('player_context_enabled')} className="mt-3">
              {t('webhook.player_context_enabled')}
            </Checkbox>
          )}
          {isSuperAdmin && (
            <div className="flex items-end gap-2">
              <Checkbox className="mt-3" {...register('enabled_logs')}>
                {t('webhook.logs.enabled')}
              </Checkbox>

              <SuperAdminInfo />
            </div>
          )}

          <Divider />

          <div className="mt-3">
            <FormProvider {...formMethods}>
              <SelectEventsButton currentWebhookId={props.webhook.id} />
            </FormProvider>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button size="sm" onClick={props.onClose} color="secondary" variant="outline" type="button">
            {t('Cancel')}
          </Button>
          <Button size="sm" color="primary" type="submit" loading={isSubmitting}>
            {t('webhook.form.save')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
